import { syncGameScore } from './dbService';

const BASE_API = process.env.REACT_APP_API_URL || '/api';

function postXp(gameId, xp) {
  const uid = localStorage.getItem('sl_user_id');
  if (!uid) return;
  fetch(`${BASE_API}/game-levels/${encodeURIComponent(uid)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ game_id: gameId, xp }),
  }).then(res => {
    if (res.ok) res.json().then(d => {
      if (d.level != null) {
        try {
          const levels = JSON.parse(localStorage.getItem('sl_game_levels') || '{}');
          const oldLevel = (levels[gameId] || {}).level || 1;
          levels[gameId] = { xp: d.xp, level: d.level };
          localStorage.setItem('sl_game_levels', JSON.stringify(levels));
          // Fire event if level increased so App.js can show a popup
          if (d.level > oldLevel) {
            window.dispatchEvent(new CustomEvent('sl:gamelevelup', {
              detail: { gameId, oldLevel, newLevel: d.level },
            }));
          }
        } catch {}
      }
    });
  }).catch(() => {});
}

export function timeAgo(time, lang) {
  if (!time) return '';
  const nl = lang === 'nl';
  let ts;
  if (typeof time === 'number') ts = time;
  else {
    const d = new Date(String(time).replace(' ', 'T'));
    if (isNaN(d.getTime())) return String(time);
    ts = d.getTime();
  }
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60)   return nl ? 'zojuist' : 'just now';
  if (diff < 3600) { const m = Math.floor(diff / 60);  return nl ? `${m} min geleden` : `${m}m ago`; }
  if (diff < 86400){ const h = Math.floor(diff / 3600); return nl ? `${h} uur geleden` : `${h}h ago`; }
  const d2 = Math.floor(diff / 86400);
  if (d2 < 7)  return nl ? `${d2} dag${d2 > 1 ? 'en' : ''} geleden` : `${d2}d ago`;
  if (d2 < 30) { const w = Math.floor(d2 / 7);  return nl ? `${w} week${w > 1 ? 'en' : ''} geleden` : `${w}w ago`; }
  const mo = Math.floor(d2 / 30);
  return nl ? `${mo} maand${mo > 1 ? 'en' : ''} geleden` : `${mo}mo ago`;
}

export function getInitials(name) {
  return (name||"").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)||"??";
}

export function calcLoad(items) {
  if (!items||!items.length) return {mental:0,physical:0,balance:80,rest:0,burnout:"low"};
  const tot = items.reduce((a,i)=>a+(i.duration||0),0);
  if (!tot) return {mental:0,physical:0,balance:80,rest:0,burnout:"low"};
  const mental = Math.min(100,Math.max(0,items.reduce((a,i)=>a+(i.mental||0)*(i.duration||0),0)/tot));
  const physical = Math.min(100,Math.max(0,items.reduce((a,i)=>a+(i.physical||0)*(i.duration||0),0)/tot));
  const restMins = items.filter(i=>i.type==="rest").reduce((a,i)=>a+(i.duration||0),0);
  const act = items.filter(i=>i.type==="activity").reduce((a,i)=>a+(i.duration||0),0);
  const rest = Math.min(100,restMins/Math.max(act,1)*200);
  const balance = Math.min(100,Math.max(0,100-(mental+physical)/2+(restMins/Math.max(act,1))*25));
  return {mental,physical,balance,rest,burnout:mental>70?"high":mental>50?"medium":"low"};
}

export function saveGameScore(gameId, rawScore) {
  try {
    const key = `sl_hist_${gameId}`;
    const prev = JSON.parse(localStorage.getItem(key) || "[]");
    prev.push({score: rawScore, ts: Date.now()});
    if (prev.length > 50) prev.splice(0, prev.length - 50);
    localStorage.setItem(key, JSON.stringify(prev));
  } catch {}
  // Optimistic local XP update so unlock gates work even before API responds
  try {
    const levels = JSON.parse(localStorage.getItem('sl_game_levels') || '{}');
    const curr = levels[gameId] || { xp: 0, level: 1 };
    const newXp = (curr.xp || 0) + 10;
    levels[gameId] = { xp: newXp, level: Math.max(curr.level || 1, Math.floor(newXp / 100) + 1) };
    localStorage.setItem('sl_game_levels', JSON.stringify(levels));
  } catch {}
  syncGameScore(gameId, rawScore).catch(() => {});
  postXp(gameId, 10); // server will correct the authoritative value
}

export function loadGameHistory(gameId) {
  try { return JSON.parse(localStorage.getItem(`sl_hist_${gameId}`) || "[]"); }
  catch { return []; }
}

export function getGameLevel(gameId) {
  try {
    const levels = JSON.parse(localStorage.getItem('sl_game_levels') || '{}');
    return levels[gameId] || { xp: 0, level: 1 };
  } catch { return { xp: 0, level: 1 }; }
}

export function weekBoundaries(weeksAgo = 0) {
  const now = Date.now();
  const msPerWeek = 7 * 24 * 3600 * 1000;
  const end = now - weeksAgo * msPerWeek;
  const start = end - msPerWeek;
  return {start, end};
}

export function avgScoreInWindow(history, start, end) {
  const entries = history.filter(e => e.ts >= start && e.ts < end);
  if (!entries.length) return null;
  return entries.reduce((a, e) => a + e.score, 0) / entries.length;
}

export function lastNScores(history, n = 8) {
  const sorted = [...history].sort((a, b) => a.ts - b.ts);
  return sorted.slice(-n).map(e => e.score);
}

/**
 * calcLoadAdjusted
 * Same as calcLoad but factors actual sleep quality and mood history
 * into the balance, rest and burnout values shown on the dashboard.
 *
 * sleepLog  – array of { date, score (1-10), hours, quality }
 * moodLog   – array of { date, score (1-5) }
 */
export function calcLoadAdjusted(items, sleepLog = [], moodLog = []) {
  const base = calcLoad(items);

  // Average sleep score across last 7 days that have data
  const last7Dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - i);
    return d.toISOString().slice(0, 10);
  });
  const recentSleep = last7Dates.map(d => sleepLog.find(s => s.date === d)).filter(Boolean);
  const avgSleepScore = recentSleep.length
    ? recentSleep.reduce((a, s) => a + (s.score ?? 5), 0) / recentSleep.length
    : null;

  // Today's mood score (1-5)
  // Fallback: derive from mood string id for legacy entries that didn't save score
  const MOOD_ID_SCORE = { great: 5, good: 4, neutral: 3, low: 2, bad: 1 };
  const todayKey   = new Date().toISOString().slice(0, 10);
  const todayMood  = moodLog.find(m => m.date === todayKey);
  const moodScore  = todayMood
    ? (todayMood.score ?? MOOD_ID_SCORE[todayMood.mood] ?? null)
    : null;

  let { mental, physical, balance, rest, burnout } = base;

  // Sleep influence: score 8+ boosts rest +15, score ≤4 reduces rest -20 and flags burnout
  if (avgSleepScore !== null) {
    const sleepDelta = (avgSleepScore - 6) * 3.5; // range ≈ -17 to +14
    rest    = Math.min(100, Math.max(0, rest + sleepDelta));
    balance = Math.min(100, Math.max(0, balance + sleepDelta * 0.5));
    if (avgSleepScore < 5)                          burnout = 'high';
    else if (avgSleepScore < 6.5 && burnout === 'low') burnout = 'medium';
  }

  // Mood influence: great (5) boosts balance +10, bad (1) reduces -10
  if (moodScore !== null) {
    const moodDelta = (moodScore - 3) * 5; // range -10 to +10
    balance = Math.min(100, Math.max(0, balance + moodDelta));
    if (moodScore <= 1 && burnout === 'low') burnout = 'medium';
  }

  return {
    mental, physical, balance, rest, burnout,
    avgSleepScore: avgSleepScore !== null ? Math.round(avgSleepScore * 10) / 10 : null,
    moodScore,
    sleepDaysLogged: recentSleep.length,
  };
}
